/**
 * Admin talk-down message library API (ENG-2179 / ENG-2232).
 * Maps snake_case responses onto the UI TalkDownMessage shape.
 */
import { createApi } from "../../utils/createApi";
import { TalkDownDispatch } from "../../common/TalkDown/talkDownApi";
import {
  TalkDownAssignment,
  TalkDownMessage,
} from "./talkDownMessageTypes";

type ApiAssignment = {
  site_id: number | string;
  site_name: string;
  mode: "entire_site" | "cameras";
  camera_ids: Array<number | string>;
  camera_names: string[];
};

type ApiMessage = {
  id: string;
  name: string;
  group_id: number | string;
  group_name: string;
  duration_seconds: number;
  recorded_by: string | null;
  recorded_at: string;
  play_count: number;
  audio_url: string | null;
  assignments: ApiAssignment[];
};

const api = () => createApi("talk_down_messages/");

const mapAssignment = (a: ApiAssignment): TalkDownAssignment => ({
  siteId: a.site_id,
  siteName: a.site_name,
  mode: a.mode,
  cameraIds: [...(a.camera_ids || [])],
  cameraNames: [...(a.camera_names || [])],
});

export const mapTalkDownMessage = (m: ApiMessage): TalkDownMessage => ({
  id: m.id,
  name: m.name,
  groupId: m.group_id,
  groupName: m.group_name,
  durationSeconds: m.duration_seconds,
  recordedBy: m.recorded_by || "",
  recordedAt: m.recorded_at,
  playCount: m.play_count ?? 0,
  audioUrl: m.audio_url,
  assignments: (m.assignments || []).map(mapAssignment),
});

const toApiAssignments = (assignments: TalkDownAssignment[]) =>
  assignments.map((a) => ({
    site_id: a.siteId,
    mode: a.mode,
    camera_ids: a.cameraIds,
  }));

export const blobUrlToFile = async (
  url: string,
  filename: string
): Promise<File> => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new File([blob], filename, {
    type: blob.type || "audio/webm",
  });
};

export const listTalkDownMessages = async (groupId?: string | number) => {
  const params = groupId != null ? { group_id: groupId } : undefined;
  const { data } = await api().get<{ results: ApiMessage[]; count: number }>(
    "",
    { params }
  );
  return (data.results || []).map(mapTalkDownMessage);
};

/** Slim row from `GET camera/{id}/talk_down_messages/` (ENG-2232 Alerts picker). */
export type CameraTalkDownMessage = {
  id: string;
  name: string;
  durationSeconds: number;
  recordedBy: string;
  recordedAt: string;
  playCount: number;
  audioUrl: string | null;
};

type ApiCameraMessage = {
  id: string;
  name: string;
  duration_seconds: number;
  recorded_by: string | null;
  recorded_at: string;
  play_count: number;
  audio_url: string | null;
};

export const mapCameraTalkDownMessage = (
  m: ApiCameraMessage
): CameraTalkDownMessage => ({
  id: m.id,
  name: m.name,
  durationSeconds: m.duration_seconds,
  recordedBy: m.recorded_by || "",
  recordedAt: m.recorded_at,
  playCount: m.play_count ?? 0,
  audioUrl: m.audio_url,
});

/** Messages assigned to this camera (entire_site or explicit cameras). */
export const listTalkDownMessagesForCamera = async (
  cameraId: string | number
): Promise<CameraTalkDownMessage[]> => {
  const { data } = await createApi("").get<ApiCameraMessage[]>(
    `camera/${cameraId}/talk_down_messages/`
  );
  const rows = Array.isArray(data) ? data : [];
  return rows.map(mapCameraTalkDownMessage);
};

/**
 * Play a stored library message on a camera (ENG-2232).
 * Same 202 body as clip talk-down — poll action_status with command_id.
 */
export const playTalkDownMessageOnCamera = async (
  cameraId: string | number,
  messageId: string
): Promise<TalkDownDispatch> => {
  const { data } = await createApi("").post(
    `camera/${cameraId}/talk_down_messages/${messageId}/play/`
  );
  return data as TalkDownDispatch;
};

export const createTalkDownMessage = async (input: {
  name: string;
  groupId: string | number;
  audio: File | Blob;
  durationSeconds: number;
  assignments?: TalkDownAssignment[];
  filename?: string;
}) => {
  const form = new FormData();
  form.append("name", input.name);
  form.append("group_id", String(input.groupId));
  form.append("duration_seconds", String(input.durationSeconds));
  const file =
    input.audio instanceof File
      ? input.audio
      : new File([input.audio], input.filename || "message.webm", {
          type: input.audio.type || "audio/webm",
        });
  form.append("audio", file);
  if (input.assignments?.length) {
    form.append(
      "assignments",
      JSON.stringify(toApiAssignments(input.assignments))
    );
  }
  const { data } = await api().post<ApiMessage>("", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return mapTalkDownMessage(data);
};

export const patchTalkDownMessage = async (
  id: string,
  patch: { name?: string; audio?: File | Blob; durationSeconds?: number }
) => {
  const form = new FormData();
  if (patch.name != null) form.append("name", patch.name);
  if (patch.durationSeconds != null) {
    form.append("duration_seconds", String(patch.durationSeconds));
  }
  if (patch.audio) {
    const file =
      patch.audio instanceof File
        ? patch.audio
        : new File([patch.audio], "message.webm", {
            type: patch.audio.type || "audio/webm",
          });
    form.append("audio", file);
  }
  const { data } = await api().patch<ApiMessage>(`${id}/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return mapTalkDownMessage(data);
};

export const putTalkDownAssignments = async (
  id: string,
  assignments: TalkDownAssignment[]
) => {
  const { data } = await api().put<ApiMessage>(`${id}/assignments/`, {
    assignments: toApiAssignments(assignments),
  });
  return mapTalkDownMessage(data);
};

export const deleteTalkDownMessage = async (id: string) => {
  await api().delete(`${id}/`);
};

/** True when the failure looks like the endpoint is not deployed yet. */
export const isTalkDownMessagesApiUnavailable = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 502 || status === 503 || !status;
};
